import React from "react";
import LandingNavbar from "../landing/LandingNavbar";
import Footer from "../landing/Footer";
import { typography, surfaces, textColors, brandColors } from "../../design-system/tokens";
import { APP_DOMAIN } from '../../core/utils/config';

const TermsOfUse = () => {
  return (
    <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
      <LandingNavbar />
      
      <div className="flex-1 pt-16">
        {/* Brand Gradient Header Section */}
        <div className={`bg-gradient-to-r ${brandColors.gradientFrom} ${brandColors.gradientTo} text-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <h1 className={typography.h1Dark}>
                Terms of Use
              </h1>
              <p className={`${typography.bodyLargeDark} mt-4`}>
                Last updated April 6, 2026
              </p>
            </div>
          </div>
        </div>

        {/* Terms of Use Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className={`${surfaces.primary} rounded-lg shadow-md p-8 space-y-8`}>
            
            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Acceptance of Terms</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  Welcome to {APP_DOMAIN} ("Company," "we," "our," or "us"). These Terms of Use ("Terms") are a legally binding agreement that govern your use of our website building platform, services, and any related applications or websites (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not access or use the Service.
                </p>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  If you use the Service on behalf of an organization, you represent and warrant that you have authority to bind that organization to these Terms, and "you" in these Terms includes that organization.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Service Description</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-4`}>
                {APP_DOMAIN} provides tools that allow users to create, customize, and share personal and professional websites. The Service may include:
              </p>
              <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                <li>Website templates and customization tools</li>
                <li>Tools to publish and share websites (where available)</li>
                <li>Storage and hosting for content you upload (where available)</li>
                <li>Customer support and service communications</li>
                <li>Additional features that may be offered from time to time</li>
              </ul>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mt-4`}>
                We may change, suspend, or discontinue any part of the Service at any time. We do not guarantee that any feature will be available at all times, that the Service will be uninterrupted, or that any content will be preserved.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>No Professional Advice; Your Compliance Responsibilities</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  The Service (including templates, examples, suggested copy, and automated features) is provided for general informational purposes and convenience only. The Company does not provide legal, tax, accounting, or other professional advice. You should consult qualified professionals for advice specific to your situation.
                </p>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  You are solely responsible for your websites and content and for complying with all applicable laws and regulations (including privacy, intellectual property, consumer protection, accessibility, and any industry-specific rules). You are also responsible for obtaining any rights, permissions, and consents needed to use and publish your content.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Eligibility and Account Registration</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Eligibility</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    You must be at least 18 years old or the age of majority in your jurisdiction to use our Service. By using our Service, you represent that you meet these age requirements.
                  </p>
                </div>
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Account Registration</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    You must provide accurate, current, and complete information during registration and maintain the security of your account credentials. You are responsible for all activities that occur under your account, including any activity by people you allow to access your account. If you believe your account has been compromised, you must notify us promptly.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Acceptable Use Policy</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-4`}>
                You agree not to use the Service for any unlawful or prohibited activities, including but not limited to:
              </p>
              <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                <li>Violating any applicable laws or regulations</li>
                <li>Infringing on intellectual property rights of others</li>
                <li>Distributing malware, viruses, or harmful code</li>
                <li>Engaging in harassment, abuse, or discriminatory conduct</li>
                <li>Publishing false, misleading, or defamatory content</li>
                <li>Attempting to gain unauthorized access to our systems</li>
                <li>Using the Service for spam or unsolicited communications</li>
                <li>Violating the privacy rights of others</li>
                <li>Interfering with or disrupting the Service (including by scraping, automated access, or excessive requests)</li>
                <li>Using the Service to collect or store personal data in violation of applicable law</li>
              </ul>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mt-4`}>
                We may remove content, disable pages, or suspend or terminate accounts that we believe violate these Terms or that create risk for the Company, our users, or third parties.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Content and Intellectual Property</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Your Content</h3>
                  <div className="space-y-3">
                    <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                      You retain ownership of content you create, upload, or otherwise make available through the Service ("User Content"). You are solely responsible for your User Content and for any consequences of publishing or sharing it.
                    </p>
                    <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                      By using the Service, you grant the Company a worldwide, non-exclusive, royalty-free license to host, store, reproduce, modify (for formatting and technical processing), display, and distribute your User Content only as necessary to operate, provide, and improve the Service and as directed by your settings (for example, if you publish a site or make it public).
                    </p>
                    <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                      You represent and warrant that you have all rights necessary to grant this license and that your User Content does not violate any law or the rights of any third party.
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Our Intellectual Property</h3>
                  <div className="space-y-3">
                    <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                      All rights, title, and interest in and to the Service (excluding User Content), including software, designs, trademarks, and proprietary technology, are and will remain the exclusive property of the Company and its licensors.
                    </p>
                    <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                      Subject to these Terms, we grant you a limited, revocable, non-transferable, non-exclusive license to access and use the Service for your internal business or personal use. You may not copy, modify, distribute, sell, lease, or reverse engineer any part of the Service except to the extent such restriction is prohibited by law.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Feedback and Suggestions</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                If you provide us with any feedback, suggestions, ideas, improvements, or other input regarding the Service ("Feedback"), you hereby assign to the Company all right, title, and interest in and to such Feedback. We are free to use, disclose, reproduce, license, or otherwise distribute and exploit such Feedback without obligation or compensation to you. This assignment applies regardless of whether the Feedback is provided in writing, orally, electronically, or by any other means.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Your Responsibility for Your Websites and Visitors</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  The Service allows you to create and publish websites that may be accessed by third-party visitors. You acknowledge and agree that:
                </p>
                <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                  <li>You are solely responsible for the content, legality, and compliance of any website you create or publish using the Service</li>
                  <li>You are the data controller (or equivalent under applicable law) for any personal data you collect from visitors to your websites, and you are solely responsible for complying with all applicable privacy and data protection laws regarding such data</li>
                  <li>You are responsible for providing your own privacy policy, cookie notice, and any other disclosures required by law on your published websites</li>
                  <li>The Company acts only as a data processor (or service provider) with respect to personal data collected through your websites, and processes such data solely on your behalf and in accordance with these Terms</li>
                  <li>You will not collect sensitive personal data (such as health, financial, or government ID information) through your websites unless you have ensured full compliance with all applicable laws and have obtained all necessary consents</li>
                </ul>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  The Company has no obligation to monitor, review, or verify the content of your websites or your compliance with applicable law, and assumes no liability for your websites or their visitors.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Privacy and Data Protection</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our Service, you acknowledge that you have read and understood our Privacy Policy and that we may collect and use your information as described therein.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Third-Party Links and Services</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  The Service may contain links to third-party websites, services, or content that are not owned or controlled by the Company. Users may also include links to third-party content in the websites they create using the Service.
                </p>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  The Company has no control over, and assumes no responsibility for, the content, privacy policies, practices, or availability of any third-party websites or services. You access third-party links at your own risk. Inclusion of any link does not imply endorsement, approval, or affiliation by the Company.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Payment Terms and Billing</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Subscription Fees</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    Certain features may require payment of subscription fees. Fees are non-refundable except as required by law or as expressly stated at the time of purchase. We may change pricing, plans, and feature availability from time to time; where required by law, we will provide notice before changes take effect.
                  </p>
                </div>
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Automatic Renewal</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    If you purchase a subscription, it may automatically renew until you cancel. You can cancel through your account settings (if available) or by contacting support. Cancellation generally takes effect at the end of the current billing period unless required otherwise by applicable law.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Service Availability and Modifications</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-4`}>
                We strive to provide reliable service but do not guarantee uninterrupted availability. We reserve the right to:
              </p>
              <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                <li>Modify, suspend, or discontinue any part of our Service</li>
                <li>Update these Terms at any time with notice</li>
                <li>Perform maintenance and updates as necessary</li>
                <li>Implement new features or remove existing ones</li>
              </ul>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Force Majeure</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                The Company shall not be liable for any delay or failure to perform any obligation under these Terms where the delay or failure results from any cause beyond our reasonable control, including but not limited to acts of God, natural disasters, pandemics or epidemics, war or terrorism, government actions or orders, labor disputes, power or internet outages, failures of third-party hosting or cloud infrastructure providers, cyberattacks, or any other force majeure event. During such events, our obligations under these Terms will be suspended for the duration of the event.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Termination and Account Deletion</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Termination by Us</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-3`}>
                    We may terminate or suspend your access immediately, with or without notice, for:
                  </p>
                  <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                    <li>Violation of these Terms</li>
                    <li>Non-payment of fees</li>
                    <li>Engaging in prohibited activities</li>
                    <li>Legal or regulatory requirements</li>
                  </ul>
                </div>
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Deletion by You</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    You may request deletion of your account at any time by submitting a request from your Account Settings page or by emailing{" "}
                    <a className="underline" href={`mailto:privacy@${APP_DOMAIN}`}>privacy@{APP_DOMAIN}</a>{" "}
                    from the email address associated with your account. We will process your request within a reasonable timeframe. Please note that some information may be retained as required by law, for legitimate business purposes (such as fraud prevention), or for the period necessary to resolve open disputes.
                  </p>
                </div>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  Upon termination or deletion, your right to access the Service ceases immediately. Sections of these Terms that by their nature should survive termination will survive, including intellectual property provisions, disclaimers, indemnification, and dispute resolution.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Disclaimers and Limitation of Liability</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Disclaimers</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF HARMFUL COMPONENTS.
                  </p>
                </div>
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Limitation of Liability</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE TWELVE MONTHS PRECEDING THE CLAIM. WE SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                </div>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  Some jurisdictions do not allow certain warranty disclaimers or liability limitations, so some of the above may not apply to you.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Indemnification</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                You agree to indemnify, defend, and hold harmless {APP_DOMAIN}, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Copyright Infringement — DMCA Notice and Takedown Policy</h2>
              <div className="space-y-4">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  {APP_DOMAIN} respects the intellectual property rights of others and complies with the Digital Millennium Copyright Act ("DMCA"), 17 U.S.C. § 512. If you believe that content accessible through the Service infringes your copyright, you may submit a takedown notice to our designated DMCA agent.
                </p>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Designated DMCA Agent</h3>
                  <div className={`p-4 ${surfaces.secondary} rounded-lg`}>
                    <p className={`${typography.body} ${textColors.primary} font-medium`}>{APP_DOMAIN} DMCA Agent</p>
                    <p className={`${typography.body} ${textColors.secondary}`}>
                      Email:{" "}
                      <a className="underline" href={`mailto:dmca@${APP_DOMAIN}`}>dmca@{APP_DOMAIN}</a>
                    </p>
                    <p className={`${typography.caption} ${textColors.tertiary} mt-2 italic`}>
                      We are in the process of registering our DMCA designated agent with the U.S. Copyright Office as required by 17 U.S.C. § 512(c)(2).
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Required Elements of a Takedown Notice</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-3`}>
                    To be valid under the DMCA, your written notice must include all of the following:
                  </p>
                  <ol className={`list-decimal list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                    <li>A physical or electronic signature of the person authorized to act on behalf of the copyright owner;</li>
                    <li>Identification of the copyrighted work(s) claimed to have been infringed (or a representative list if multiple);</li>
                    <li>Identification of the allegedly infringing material and information reasonably sufficient for us to locate it (e.g., URL);</li>
                    <li>Your contact information (name, address, telephone number, and email address);</li>
                    <li>A statement that you have a good-faith belief that use of the material is not authorized by the copyright owner, its agent, or the law; and</li>
                    <li>A statement, made under penalty of perjury, that the information in the notification is accurate and that you are the copyright owner or authorized to act on the owner's behalf.</li>
                  </ol>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Counter-Notice Procedure</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    If you believe that your content was removed or disabled by mistake or misidentification, you may submit a counter-notice to our DMCA agent containing: (a) your physical or electronic signature; (b) identification of the removed material and its location before removal; (c) a statement under penalty of perjury that you have a good-faith belief the material was removed by mistake or misidentification; and (d) your name, address, and phone number, and a statement consenting to jurisdiction of the federal district court for your address (or any judicial district if outside the U.S.). We will forward the counter-notice to the original complainant and, unless the complainant files a court action within 10–14 business days, we may restore the content.
                  </p>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Repeat Infringer Policy</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    In accordance with the DMCA and other applicable laws, we have adopted a policy of terminating, in appropriate circumstances, accounts of users who are deemed repeat infringers. We may also, at our sole discretion, limit or terminate access to the Service for users who infringe any intellectual property rights of others.
                  </p>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Good-Faith Submissions Only</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    Please be aware that under 17 U.S.C. § 512(f), any person who knowingly and materially misrepresents that material is infringing, or that material was removed by mistake, may be liable for damages, including costs and attorneys' fees.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Dispute Resolution</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Governing Law</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    These Terms are governed by and construed in accordance with the laws of the State of Delaware and the United States of America, without regard to conflict of law principles. If the Company is incorporated in a different state, the laws of that state will apply instead.
                  </p>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Mandatory Binding Arbitration</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-3`}>
                    PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.
                  </p>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-3`}>
                    Except as set out below, you and the Company agree that all disputes, claims, or controversies arising out of or relating to these Terms, the Service, or the relationship between you and the Company (collectively, "Disputes") shall be resolved exclusively by final and binding individual arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules (available at <a href="https://www.adr.org" target="_blank" rel="noopener noreferrer" className="underline">www.adr.org</a>), as modified by these Terms. The arbitration shall be conducted in English. The arbitrator's decision will be final and binding and may be entered as a judgment in any court of competent jurisdiction.
                  </p>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    Exceptions — arbitration does not apply to: (a) claims that qualify for small claims court (where both parties meet the eligibility requirements); (b) claims for injunctive or equitable relief related to intellectual property infringement, unauthorized access, or misuse of confidential information; and (c) claims where applicable law prohibits mandatory arbitration.
                  </p>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Class Action and Jury Trial Waiver</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, YOU AND THE COMPANY EACH WAIVE THE RIGHT TO A TRIAL BY JURY AND THE RIGHT TO PARTICIPATE IN A CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE ACTION OR ARBITRATION. Each Dispute must be brought on an individual basis only. If a court or arbitrator finds this waiver unenforceable as to a particular claim, that claim must be severed and litigated in court while all remaining claims proceed to arbitration.
                  </p>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Arbitration Opt-Out</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    You may opt out of the arbitration agreement by sending written notice to{" "}
                    <a className="underline" href={`mailto:legal@${APP_DOMAIN}`}>legal@{APP_DOMAIN}</a>{" "}
                    within thirty (30) days of first creating your account or first agreeing to these Terms (whichever is earlier). Your notice must include your name, email address associated with your account, and a clear statement that you are opting out of the arbitration agreement. Opting out will not affect any other aspect of these Terms.
                  </p>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Arbitration Fees</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    Arbitration filing fees will be allocated according to the applicable AAA Consumer Arbitration Rules. If the arbitrator determines that a claim was frivolous or brought in bad faith, the arbitrator may award attorneys' fees and costs to the other party.
                  </p>
                </div>

                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Informal Resolution First</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    Before initiating arbitration or small claims proceedings, you agree to first contact us at{" "}
                    <a className="underline" href={`mailto:legal@${APP_DOMAIN}`}>legal@{APP_DOMAIN}</a>{" "}
                    and provide a written description of the Dispute, the relief sought, and the contact information of the claimant. We will attempt to resolve the Dispute informally within thirty (30) days. If we cannot reach a resolution, either party may proceed to arbitration.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Electronic Communications</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                By using the Service, you acknowledge and agree that we may send communications to you electronically, including by email and through notices posted on the Service. You agree that all agreements, notices, disclosures, and other communications we provide electronically satisfy any legal requirement that such communications be in writing.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>General Provisions</h2>
              <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                <li>These Terms constitute the entire agreement between you and {APP_DOMAIN}</li>
                <li>If any provision is found unenforceable, the remainder shall remain in effect</li>
                <li>We may assign these Terms; you may not assign them without our consent</li>
                <li>No waiver of any term shall be deemed a continuing waiver</li>
                <li>Headings are for convenience only and do not affect interpretation</li>
              </ul>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Contact Information</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                If you have any questions about these Terms of Use, please contact us at:
              </p>
              <div className={`mt-4 p-4 ${surfaces.secondary} rounded-lg`}>
                <p className={`${typography.body} ${textColors.primary} font-medium`}>{APP_DOMAIN} Legal Team</p>
                <p className={`${typography.body} ${textColors.secondary}`}>
                  Email:{" "}
                  <a className="underline" href={`mailto:legal@${APP_DOMAIN}`}>
                    legal@{APP_DOMAIN}
                  </a>
                </p>
                <p className={`${typography.body} ${textColors.secondary}`}>
                  We try to respond as soon as reasonably practical.
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsOfUse;